import {
  ArrowLeft,
  CalendarPlus,
  Check,
  ExternalLink,
  FileText,
  Copy,
  Pencil,
  Share2,
} from "lucide-react";
import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useApp } from "../domain/AppContext";
import { compareClassNames, formatClassName } from "../domain/classCode";
import { canStudentEnroll, formatPhaseDate, getPhaseDateRange, isPaidOlympiad, isStudentEligible, resolvePhaseFee } from "../domain/rules";
import { Button, StatusChip } from "../components/ui";
import { OlympiadAreaIcon } from "../components/OlympiadAreaIcon";

type DetailTab =
  | "overview"
  | "schedule"
  | "rules"
  | "enrollment"
  | "results";

export function OlympiadDetailPage() {
  const { slug } = useParams();
  const { profile, data, notify, cancelEnrollment, setOlympiadPublished } = useApp();
  const [tab, setTab] = useState<DetailTab>("overview");
  const [cancelling, setCancelling] = useState(false);
  if (!profile || !data) return null;
  const olympiad = data.olympiads.find((item) => item.slug === slug);
  if (!olympiad) return <Navigate to="/olimpiadas" replace />;
  const enrollment = data.enrollments.find(
    (item) =>
      item.olympiadId === olympiad.id &&
      item.studentIds.includes(profile.id) &&
      item.status !== "cancelled",
  );
  const olympiadResults = data.results.filter((item) => item.olympiadId === olympiad.id && (profile.role !== 'student' || (item.studentId === profile.id && item.published)));
  const isStudent = profile.role === "student";
  const student = data.students.find((item) => item.id === profile.id);
  const eligible =
    !isStudent || Boolean(student && isStudentEligible(olympiad, student));
  const tabOptions: {
    id: DetailTab;
    label: string;
    count?: number;
    disabled?: boolean;
  }[] = [
    { id: "overview", label: "Visão geral" },
    { id: "schedule", label: "Cronograma" },
    { id: "rules", label: "Regulamento" },
    ...(isStudent
      ? [
          {
            id: "enrollment" as DetailTab,
            label: "Minha inscrição",
            disabled: !enrollment,
          },
        ]
      : []),
    { id: "results", label: "Resultados", count: olympiadResults.length, disabled: !olympiadResults.length },
  ];

  const share = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      notify({
        type: "success",
        title: "Link copiado",
        description: "Compartilhe o endereço da olimpíada.",
      });
    } catch {
      notify({ type: "error", title: "Não foi possível copiar o link" });
    }
  };

  const cancel = async () => {
    if (!enrollment) return;
    setCancelling(true);
    const result = await cancelEnrollment(enrollment.id);
    setCancelling(false);
    notify(
      result.ok
        ? { type: "success", title: "Inscrição cancelada" }
        : { type: "error", title: result.error.message },
    );
    if (result.ok) setTab("overview");
  };
  const togglePublication = async () => {
    const result = await setOlympiadPublished(olympiad.id, !olympiad.published);
    notify(result.ok ? { type: 'success', title: result.data.published ? 'Olimpíada publicada para os estudantes' : 'Olimpíada retirada do catálogo dos estudantes' } : { type: 'error', title: 'Publicação não alterada', description: result.error.message });
  };

  return (
    <div className="olympiad-detail-page">
      <Link className="back-link" to="/olimpiadas">
        <ArrowLeft size={17} /> Voltar ao catálogo
      </Link>
      <section className="olympiad-detail-hero">
        <OlympiadAreaIcon olympiad={olympiad} size="hero" />
        <div className="detail-heading">
          <div>
            <StatusChip status={enrollment?.status ?? olympiad.status} />
            <span>{olympiad.area}</span>
          </div>
          <h1>{olympiad.name}</h1>
          <p>{[olympiad.organizer, olympiad.modality, olympiad.eligibleGrades.length ? `${olympiad.eligibleGrades[0]} ao ${olympiad.eligibleGrades.at(-1)}` : null].filter(Boolean).join(" · ")}</p>
        </div>
        <div className="detail-actions">
          {profile.permissions.includes('*') || profile.permissions.includes('olympiads:manage') || profile.permissions.includes('olympiads:create') || profile.permissions.includes('olympiads:publish') ? <Button variant={olympiad.published ? 'ghost' : 'primary'} onClick={() => void togglePublication()}>{olympiad.published ? 'Retirar publicação' : 'Publicar'}</Button> : null}
          {profile.permissions.includes('*') || profile.permissions.includes('olympiads:manage') || profile.permissions.includes('olympiads:create') || profile.permissions.includes('olympiads:publish') ? <><Link className="button button-secondary" to={`/olimpiadas/${olympiad.slug}/editar`}><Pencil size={16} /> Editar</Link><Link className="button button-ghost" to={`/olimpiadas/nova?duplicar=${olympiad.slug}`}><Copy size={16} /> Duplicar</Link></> : null}
          {isStudent &&
          eligible &&
          student &&
          canStudentEnroll(olympiad, student) &&
          olympiad.status === "open" &&
          !enrollment ? (
            <Link
              className="button button-primary"
              to={`/olimpiadas/${olympiad.slug}/inscricao`}
            >
              Quero participar
            </Link>
          ) : null}
          <Button variant="secondary" onClick={share}>
            <Share2 size={17} /> Compartilhar
          </Button>
        </div>
      </section>
      {isStudent ? (
        <div className={`eligibility-banner ${eligible ? "" : "ineligible"}`}>
          {eligible ? <Check size={18} /> : <span aria-hidden="true">!</span>}
          <strong>
            {eligible
              ? "Você é elegível"
              : "Esta olimpíada não está disponível para sua turma"}
          </strong>
          <span>
            {formatClassName(profile.className)} ·{" "}
            {eligible ? "requisitos atendidos" : "inscrição bloqueada"}
          </span>
        </div>
      ) : null}
      <nav className="detail-tabs" aria-label="Conteúdo da olimpíada">
        {tabOptions.map((item) => (
          <button
            key={item.id}
            type="button"
            className={tab === item.id ? "active" : ""}
            disabled={item.disabled}
            onClick={() => setTab(item.id)}
          >
            {item.label}
            {item.count !== undefined ? <span>{item.count}</span> : null}
          </button>
        ))}
      </nav>
      <div className="detail-content">
        {tab === "overview" ? (
          <Overview olympiad={olympiad} student={student} />
        ) : null}
        {tab === "schedule" ? <Schedule olympiad={olympiad} student={student} showAllClasses={!isStudent} /> : null}
        {tab === "rules" ? <Rules olympiad={olympiad} /> : null}
        {tab === "results" ? <section><div className="section-heading"><div><p className="eyebrow">Resultados publicados</p><h2>Desempenho nesta olimpíada</h2></div></div><div className="result-detail-list">{olympiadResults.map((result) => { const resultStudent = data.students.find((item) => item.id === result.studentId); const phase = olympiad.phases.find((item) => item.id === result.phaseId); return <article key={result.id}><span className="avatar">{resultStudent?.initials}</span><div><strong>{profile.role === 'student' ? phase?.title : resultStudent?.name}</strong><p>{phase?.title} · {result.score === null ? 'Nota não lançada' : `Nota ${result.score}`}</p></div><span className="status-chip status-success">{result.award || result.classification || 'Registrado'}</span></article>; })}</div></section> : null}
        {tab === "enrollment" && enrollment ? (
          <section className="enrollment-detail">
            <div className="section-heading">
              <div>
                <p className="eyebrow">Situação da inscrição</p>
                <h2>
                  <StatusChip status={enrollment.status} />
                </h2>
              </div>
            </div>
            <div className="detail-data-grid">
              <div>
                <span>Protocolo</span>
                <strong>{enrollment.protocol}</strong>
              </div>
              <div>
                <span>Solicitada em</span>
                <strong>
                  {new Date(enrollment.requestedAt).toLocaleString("pt-BR")}
                </strong>
              </div>
              <div>
                <span>Confirmação</span>
                <strong>
                  {enrollment.approvedBy ??
                    (enrollment.paymentStatus === "pending"
                      ? "Aguardando pagamento"
                      : "Pendente")}
                </strong>
              </div>
              <div>
                <span>Local da prova</span>
                <strong>{olympiad.examLocation}</strong>
              </div>
            </div>
            <div className="history-list">
              <h3>Histórico</h3>
              <p>
                <span />
                Você solicitou a inscrição.
              </p>
              {enrollment.approvedAt ? (
                <p>
                  <span />
                  Inscrição aprovada por {enrollment.approvedBy}.
                </p>
              ) : (
                <p>
                  <span />
                  {enrollment.paymentStatus === "pending"
                    ? "Aguardando confirmação do pagamento."
                    : "Aguardando informação necessária."}
                </p>
              )}
            </div>
            {enrollment.createdBy === "student" ? (
              <Button variant="danger" loading={cancelling} onClick={cancel}>
                Cancelar inscrição
              </Button>
            ) : (
              <p className="muted-note">
                Esta inscrição foi realizada por um professor. Fale com a
                coordenação para solicitar alteração.
              </p>
            )}
          </section>
        ) : null}
      </div>
    </div>
  );
}

function Overview({
  olympiad,
  student,
}: {
  olympiad: NonNullable<ReturnType<typeof useApp>["data"]>["olympiads"][number];
  student?: NonNullable<ReturnType<typeof useApp>["data"]>["students"][number];
}) {
  const firstPhaseFee = student && olympiad.phases[0]
    ? resolvePhaseFee(olympiad, student, olympiad.phases[0])
    : olympiad.cost;
  return (
    <div className="detail-overview-grid">
      <section>
        <p className="eyebrow">Sobre a competição</p>
        <h2>Visão geral</h2>
        {olympiad.description || olympiad.additionalInfo ? <p className="long-copy">{[olympiad.description, olympiad.additionalInfo].filter(Boolean).join(" ")}</p> : null}
        <div className="fact-grid">
          <div>
            <span>Questões</span>
            <strong>30 objetivas</strong>
          </div>
          <div>
            <span>Duração</span>
            <strong>3 horas</strong>
          </div>
          <div>
            <span>Aplicação</span>
            <strong>{olympiad.modality}</strong>
          </div>
          <div>
            <span>Custo</span>
            <strong>
              {isPaidOlympiad(olympiad)
                ? student
                  ? `R$ ${firstPhaseFee.toFixed(2).replace(".", ",")} na primeira fase`
                  : olympiad.feeVariationByPhase || olympiad.feeVariationByClass
                    ? "Varia por fase ou turma"
                    : `R$ ${olympiad.cost.toFixed(2).replace(".", ",")}`
                : "Gratuita"}
            </strong>
          </div>
        </div>
        <div className="requirements">
          <h3>Requisitos para participar</h3>
          {[
            "Matrícula ativa na série elegível",
            "Dados cadastrais completos",
            "Autorização do responsável em arquivo",
            "Inscrições dentro do prazo",
          ].map((item) => (
            <p key={item}>
              <Check size={16} /> {item}
            </p>
          ))}
        </div>
      </section>
      <aside>
        <div className="next-steps">
          <h3>Próximos passos</h3>
          <ol>
            <li>
              <span>1</span>
              <div>
                <strong>Solicite a inscrição</strong>
                <p>Leva cerca de 3 minutos.</p>
              </div>
            </li>
            <li>
              <span>2</span>
              <div>
                <strong>Aprovação da escola</strong>
                <p>Em até 2 dias úteis.</p>
              </div>
            </li>
            <li>
              <span>3</span>
              <div>
                <strong>Prepare-se para a prova</strong>
                <p>Confira o cronograma e o regulamento.</p>
              </div>
            </li>
          </ol>
        </div>
        <div className="school-proof">
          <h3>Na sua escola</h3>
          <p>
            <strong>{olympiad.registrations}</strong> estudantes inscritos
          </p>
          <p>
            <strong>7</strong> colegas da sua turma
          </p>
          <p>
            Responsável: <strong>Prof. Guibson</strong>
          </p>
        </div>
      </aside>
    </div>
  );
}

function Schedule({
  olympiad,
  student,
  showAllClasses,
}: {
  olympiad: NonNullable<ReturnType<typeof useApp>["data"]>["olympiads"][number];
  student?: NonNullable<ReturnType<typeof useApp>["data"]>["students"][number];
  showAllClasses: boolean;
}) {
  return (
    <section className="schedule-section">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Fonte oficial</p>
          <h2>Cronograma da {olympiad.acronym} 2026</h2>
        </div>
        <span className="sync-label">Dados locais</span>
      </div>
      <div className="schedule-timeline">
        {olympiad.phases.map((phase) => {
          const className = student?.className;
          const classSchedules = phase.datesVaryByClass && showAllClasses
            ? [...(olympiad.eligibleClasses ?? [])].sort(compareClassNames).map((name) => ({ name, date: formatPhaseDate(phase, name) }))
            : [];
          const phaseFee = student ? resolvePhaseFee(olympiad, student, phase) : 0;
          return <article key={phase.id} className={phase.status}>
            <span className="schedule-marker">
              {phase.status === "done" ? "✓" : ""}
            </span>
            <div>
              <p className="eyebrow">
                {phase.status === "next"
                  ? "Próxima etapa"
                  : phase.status === "done"
                    ? "Concluído"
                    : "Previsto"}
              </p>
              <h3>{phase.title}</h3>
              <p>
                {classSchedules.length ? "Datas definidas por turma" : formatPhaseDate(phase, className)}
                {phase.time ? ` · ${phase.time}` : ""}
                {phase.location ? ` · ${phase.location}` : ""}
              </p>
              {classSchedules.length ? <ul className="schedule-class-dates">{classSchedules.map((item) => <li key={item.name}><strong>{formatClassName(item.name)}</strong><span>{item.date}</span></li>)}</ul> : null}
              {student && isPaidOlympiad(olympiad) ? <small className="phase-student-fee">Valor para sua turma: <strong>R$ {phaseFee.toFixed(2).replace(".", ",")}</strong></small> : null}
            </div>
            {phase.status === "next" && !classSchedules.length ? (
              <Button variant="secondary" onClick={() => downloadPhaseCalendar(olympiad, phase, className)}>
                <CalendarPlus size={17} /> Adicionar ao calendário
              </Button>
            ) : null}
          </article>;
        })}
      </div>
    </section>
  );
}

function downloadPhaseCalendar(
  olympiad: NonNullable<ReturnType<typeof useApp>["data"]>["olympiads"][number],
  phase: NonNullable<ReturnType<typeof useApp>["data"]>["olympiads"][number]["phases"][number],
  className?: string,
) {
  const range = getPhaseDateRange(phase, className);
  if (!range.date) return;
  const compact = (value: string) => value.replaceAll("-", "");
  const exclusiveEnd = new Date(`${range.endDate || range.date}T12:00:00`);
  exclusiveEnd.setDate(exclusiveEnd.getDate() + 1);
  const end = exclusiveEnd.toLocaleDateString("sv-SE");
  const escape = (value: string) => value.replaceAll("\\", "\\\\").replaceAll(",", "\\,").replaceAll(";", "\\;").replaceAll("\n", "\\n");
  const content = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Oficina Sapiens//Olimpiadas//PT-BR",
    "BEGIN:VEVENT",
    `UID:${phase.id}-${className ?? "geral"}@oficinasapiens`,
    `DTSTART;VALUE=DATE:${compact(range.date)}`,
    `DTEND;VALUE=DATE:${compact(end)}`,
    `SUMMARY:${escape(`${olympiad.acronym} · ${phase.title}`)}`,
    `LOCATION:${escape(phase.location ?? "")}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  const url = URL.createObjectURL(new Blob([content], { type: "text/calendar;charset=utf-8" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${olympiad.slug}-${phase.id}.ics`;
  anchor.click();
  URL.revokeObjectURL(url);
}

function Rules({
  olympiad,
}: {
  olympiad: NonNullable<ReturnType<typeof useApp>["data"]>["olympiads"][number];
}) {
  const openSummary = () => {
    const content = `<!doctype html><html lang="pt-BR"><meta charset="utf-8"><title>Resumo ${olympiad.acronym} 2026</title><style>body{font:16px/1.6 system-ui;max-width:760px;margin:48px auto;padding:0 24px;color:#18233d}h1{color:#173f91}</style><h1>${olympiad.name}</h1><p>${olympiad.additionalInfo}</p><h2>Participação</h2><ul><li>Confira datas, horários e local no cronograma oficial exibido na plataforma.</li><li>Os dados cadastrais precisam estar completos e atualizados.</li><li>${isPaidOlympiad(olympiad) ? "A confirmação ocorre após o registro de todas as cobranças exigíveis." : "A inscrição gratuita é confirmada automaticamente."}</li><li>Em equipes, todos os integrantes precisam pertencer a turmas elegíveis.</li></ul><p>Este resumo institucional não substitui o regulamento oficial.</p>`;
    const url = URL.createObjectURL(new Blob([content], { type: "text/html;charset=utf-8" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `resumo-${olympiad.slug}.html`;
    anchor.click();
    URL.revokeObjectURL(url);
  };
  return (
    <section className="rules-panel">
      <div className="file-preview">
        <FileText size={30} />
        <div>
          <strong>Regulamento {olympiad.acronym} 2026</strong>
          <p>Resumo institucional · HTML acessível</p>
        </div>
        <Button variant="secondary" onClick={openSummary}>
          <ExternalLink size={17} /> Baixar resumo
        </Button>
      </div>
      <h2>Resumo para participação</h2>
      <p>{olympiad.additionalInfo}</p>
      <ul>
        <li>Confira as datas e os horários antes de solicitar a inscrição.</li>
        <li>Dados cadastrais precisam estar completos e atualizados.</li>
        <li>{isPaidOlympiad(olympiad) ? "A confirmação ocorre após o registro das cobranças exigíveis." : "A confirmação é automática porque a participação é gratuita."}</li>
        <li>
          Em competições por equipe, todos os integrantes precisam ser
          elegíveis.
        </li>
      </ul>
    </section>
  );
}
